import React, { useMemo } from 'react';

const ActivityHeatmap = ({ data = [], totalCount = 0, months = 6 }) => {
    // Generate date range
    const days = useMemo(() => {
        const result = [];
        const today = new Date();
        const startDate = new Date();
        startDate.setMonth(today.getMonth() - months);

        // Adjust to the start of the week (Sunday)
        const dayOfWeek = startDate.getDay();
        startDate.setDate(startDate.getDate() - dayOfWeek);

        const curr = new Date(startDate);
        while (curr <= today) {
            const dateStr = curr.toISOString()?.split('T')?.[0];
            const activity = data?.find(d => d.activity_date?.split('T')?.[0] === dateStr);
            result.push({
                date: dateStr,
                count: activity ? activity.activity_count : 0,
                dayOfWeek: curr.getDay(),
                month: curr.toLocaleString('default', { month: 'short' })
            });
            curr.setDate(curr.getDate() + 1);
        }
        return result;
    }, [data, months]);

    const getColor = (count) => {
        if (count === 0) return 'bg-slate-100';
        if (count === 1) return 'bg-emerald-200';
        if (count === 2) return 'bg-emerald-400';
        if (count <= 4) return 'bg-emerald-500';
        return 'bg-emerald-600';
    };

    // Group days into weeks for column layout
    const weeks = [];
    for (let i = 0; i < days.length; i += 7) {
        weeks.push(days.slice(i, i + 7));
    }

    const monthLabels = useMemo(() => {
        const labels = [];
        let lastMonth = "";
        weeks.forEach((week, i) => {
            const firstDayOfMonth = week[0].month;
            if (firstDayOfMonth !== lastMonth) {
                labels.push({ label: firstDayOfMonth, index: i });
                lastMonth = firstDayOfMonth;
            }
        });
        return labels;
    }, [weeks]);

    return (
        <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-[14px] font-normal text-slate-900">{totalCount.toLocaleString()} Mock Sessions in the last year</h3>
                <div className="flex items-center gap-2 text-[11px] font-normal text-slate-400">
                    <span>Less</span>
                    <div className="flex gap-1">
                        <div className="w-3 h-3 rounded-sm bg-slate-100" />
                        <div className="w-3 h-3 rounded-sm bg-emerald-200" />
                        <div className="w-3 h-3 rounded-sm bg-emerald-400" />
                        <div className="w-3 h-3 rounded-sm bg-emerald-500" />
                        <div className="w-3 h-3 rounded-sm bg-emerald-600" />
                    </div>
                    <span>More</span>
                </div>
            </div>

            <div className="relative overflow-x-auto pb-2 custom-scrollbar">
                <div className="min-w-[700px]">
                    {/* Month Labels */}
                    <div className="flex mb-2 text-[11px] text-slate-500 h-4">
                        <div className="w-8 shrink-0" />
                        <div className="flex-1 flex" style={{ display: 'grid', gridTemplateColumns: `repeat(${weeks.length}, 1fr)` }}>
                            {monthLabels.map((m, i) => (
                                <div key={i} style={{ gridColumnStart: m.index + 1 }} className="whitespace-nowrap">
                                    {m.label}
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="flex gap-2">
                        {/* Day Labels */}
                        <div className="flex flex-col justify-between text-[11px] text-slate-500 w-8 py-1">
                            <span>Mon</span>
                            <span>Wed</span>
                            <span>Fri</span>
                        </div>

                        {/* The Grid */}
                        <div className="flex-1 grid grid-flow-col gap-1.5" style={{ gridTemplateRows: 'repeat(7, 1fr)' }}>
                            {days.map((day, i) => (
                                <div
                                    key={i}
                                    className={`w-[14px] h-[14px] rounded-[3px] transition-all hover:ring-2 hover:ring-emerald-400/40 cursor-pointer ${getColor(day.count)}`}
                                    title={`${day.date}: ${day.count} sessions`}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ActivityHeatmap;
